/**
 * The two refusals every MCP write shares, in one place.
 *
 * Both were written for board writes in tranche 1 and both apply, unchanged, to
 * deletion — so this is the tranche-1 predicate moved rather than a second one
 * that can drift into disagreeing with it.
 *
 * 1. **A scope we cannot confirm is REFUSED, never redirected.**
 *    `scopeKeyFor('team:99')` returns `"team:99"` for any string, and
 *    `recordsContext` is equally happy — neither has a membership check. So an
 *    agent naming a team the user is not in, or naming a real team in the
 *    seconds before the background teams fetch lands, would write (or tombstone)
 *    rows in a workspace that never syncs and never renders. Those rows are not
 *    lost, they are invisible, which is worse than an error.
 *
 * 2. **A viewer on a team scope is REFUSED.** The server rejects a viewer's
 *    push, and a server-rejected push poisons that scope's outbox permanently
 *    with no event reaching JS — unrecoverable from the client, and it takes
 *    every later write in that scope down with it. The board UI already mirrors
 *    the server rule; this is the same mirror, through the same predicate, for
 *    the writer that has no UI. A DELETE is if anything worse than an edit here:
 *    the local tombstone is applied optimistically, so the user sees the record
 *    disappear from a scope where the deletion will never be accepted.
 */
import { isTeamViewer } from '../extensions/bundled/task-manager/boardsStore';
import { resolveScopes, PERSONAL } from './scopes';
import { mcpError } from './errors';

/**
 * `null` when `wire` may be written, or the tool-execution error to return.
 * Nothing has been read or written by the time this answers.
 */
export function refuseUnwritableScope(wire) {
  const { consulted } = resolveScopes(wire);
  if (!consulted.includes(wire)) {
    return mcpError(
      'unavailable',
      'That scope is not reachable right now, so nothing was written. Call scopes_list and retry with a scope it names.',
      { scope: wire }
    );
  }
  if (wire !== PERSONAL) {
    const teamId = wire.slice('team:'.length);
    if (isTeamViewer(teamId)) {
      return mcpError(
        'unavailable',
        'The user has view-only access to this team, so nothing here can be changed. Nothing was written.',
        { scope: wire }
      );
    }
  }
  return null;
}

/** Uniform scope accounting on a write, so a client never special-cases. */
export function scopeReport(wire) {
  const { consulted, unavailable } = resolveScopes(wire);
  return { scopesConsulted: consulted, scopesUnavailable: unavailable };
}
